<template>
  <div class="view-job-find">
    <b-container>
      <b-row>
        <b-col class="pt-10 pb-20 py-lg-40">
          <SearchBar
            v-model="searchForm"
            search-placeholder="Job title or keyword"
            location-placeholder="City, area or select remote"
            light-shadow
            include-remote-in-country-selector
            :on-submit="onSearchBarSubmit"
          />
        </b-col>
      </b-row>
      <b-row ref="list" class="pb-60">
        <b-col lg="3">
          <div class="d-flex d-lg-block justify-content-between">
            <div>
              <SearchFilter
                v-model="filters.roles"
                label="Roles"
                :options="jobRoles"
                :display-limit="22"
              />

              <SearchFilter
                v-model="filters.positionTypes"
                label="Job types"
                :options="jobPositionTypes"
                value-key="id"
              />

              <SearchFilter
                v-model="filters.countryCode"
                label="Remote"
                :options="isRemote"
                single-selection
              />
            </div>
          </div>
        </b-col>

        <b-col>
          <header
            class="
                    d-block d-sm-flex
                    align-items-center
                    justify-content-between
                    mt-16 mt-lg-0
                    pt-16 pt-lg-0
                    pb-4 pb-lg-10
                    border-md-down-top
                  "
          >
            <h6 class="pr-24 pb-16 pb-sm-0">
              <span class="hidden-md-down">Showing:</span>
              {{ jobs ? jobs.total : 0 }}
              <span class="hidden-md-down">filtered</span> jobs
            </h6>

            <JobsNewsletter v-if="jobs.docs.length" :filters="filters" />

            <dropdown
              :options="jobSorting.options"
              :selected="jobSorting.options[sorting]"
              @change="selectSorting"
            />
          </header>

          <content>
            <SearchNoResults v-if="!jobs.docs.length" type="jobs" />
            <h6 v-if="!jobs.docs.length" class="pb-8">Showing latest jobs</h6>

            <div v-for="job in list" :key="job.id" class="my-10">
              <JobsListItem :job="job" />
            </div>
          </content>

          <footer v-if="jobs && jobs.pages > 1" class="mt-40">
            <SearchPagination v-model="page" :result="jobs" />
          </footer>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import get from 'lodash/get'
import clone from 'lodash/clone'
import omitBy from 'lodash/omitBy'
import castArray from 'lodash/castArray'
import isEqual from 'lodash/isEqual'
import debounce from 'lodash/debounce'
import Dropdown from '@/components/Dropdown'
import SearchBar from '@/components/search/SearchBar'
import SearchFilter from '@/components/search/SearchFilter'
import JobsListItem from '@/components/jobs/JobsListItem'
import SearchNoResults from '@/components/search/SearchNoResults'
import SearchPagination from '@/components/search/SearchPagination'
import JobsNewsletter from '@/components/jobs/JobsNewsletter'
import { isEmptyDeep } from '@/helpers/helpers'
import { buildMetaHead } from '@/helpers/meta'

export default {
  components: {
    Dropdown,
    SearchBar,
    SearchFilter,
    JobsListItem,
    SearchPagination,
    SearchNoResults,
    JobsNewsletter
  },
  head() {
    return buildMetaHead({
      title: 'Part time jobs in Stockholm - Startup jobs at the Hub',
      description:
        'Discover the wide range of part-time jobs in Stockholm. At the Hub, you can find the most exciting startup jobs. So why wait? Check them out!',
      path: this.$route.path,
      imageUrl:
        process.env.BASE_URL + '/images/jobs/Part_time_jobs_in_Stockholm.png'
    })
  },
  data() {
    const countryCode = get(
      this.$route,
      'query.countryCode',
      this.$store.getters['meta/sitePreferredCountryCode']
    )

    return {
      page: parseInt(get(this.$route, 'query.page', 1)),
      initialFilters: {
        positionTypes: [],
        roles: [],
        search: undefined,
        location: undefined,
        countryCode
      },
      filters: {
        positionTypes: castArray(get(this.$route, 'query.positionTypes', [])),
        roles: castArray(get(this.$route, 'query.roles', [])),
        search: get(this.$route, 'query.search'),
        location: get(this.$route, 'query.location'),
        countryCode
      },
      searchForm: {
        search: get(this.$route, 'query.search'),
        location: get(this.$route, 'query.location'),
        countryCode
      },
      sorting:
        get(this.$route, 'query.sorting') ||
        this.$cookies.get('jobSorting') ||
        this.$store.getters['meta/jobSorting'].default
    }
  },
  computed: {
    queryIsRemote() {
      return 'countryCode' in this.params &&
        this.params.countryCode === 'REMOTE'
        ? true
        : false
    },
    params() {
      const params = omitBy(clone(this.filters), isEmptyDeep)
      if (this.page >= 2) {
        params.page = this.page
      } else {
        delete params.page
      }
      params.sorting = this.sorting

      return params
    },
    jobs() {
      return this.$store.state.jobs.jobs
    },
    latestJobs() {
      return this.$store.state.jobs.latestJobs
    },
    list() {
      return this.jobs.total ? this.jobs.docs : this.latestJobs.docs
    },
    isRemote() {
      return [
        {
          label: 'Remote jobs only',
          value: 'REMOTE',
          count: get(this.jobs, 'suggestions.remote', undefined)
        }
      ]
    },
    jobPositionTypes() {
      const jobPositionTypes = this.$store.getters['meta/jobPositionTypes']

      return !this.jobs.total
        ? jobPositionTypes
        : jobPositionTypes.map((item) => ({
            ...item,
            count: get(
              this.jobs,
              'suggestions.jobPositionTypes.' + item.id,
              undefined
            )
          }))
    },
    jobSorting() {
      return this.$store.getters['meta/jobSorting']
    },
    jobRoles() {
      return !this.jobs.total
        ? this.$store.getters['meta/jobRoles']
        : this.$store.getters['meta/jobRoles'].map((item) => ({
            ...item,
            count: get(
              this.jobs,
              'suggestions.jobRoles.' + item.value,
              undefined
            )
          }))
    }
  },
  watch: {
    'searchForm.countryCode'(newValue, oldValue) {
      /**
       * trigger search whenever we change the country.
       */
      if (newValue !== oldValue) {
        this.filters.countryCode = this.searchForm.countryCode
        this.$store.dispatch(
          'meta/setPreferredCountryCode',
          this.filters.countryCode
        )
      }
    },
    filters: {
      deep: true,
      handler(newVal, oldVal) {
        // whenever a filter is changed, reset offset (page) to beginning
        if (newVal.page === oldVal.page) {
          this.page = 1
        }

        if (this.filters.search !== this.searchForm.search) {
          this.searchForm.search = this.filters.search
        }

        if (this.filters.location !== this.searchForm.location) {
          this.searchForm.location = this.filters.location
        }

        if (this.filters.countryCode === null) {
          this.filters.countryCode = this.$store.getters[
            'meta/sitePreferredCountryCode'
          ]
        }

        if (this.filters.countryCode !== this.searchForm.countryCode) {
          this.searchForm.countryCode = this.filters.countryCode
        }

        this.doSearch()
      }
    },
    $route() {
      if (!isEqual(this.$route.query, this.params)) {
        this.filters = {
          ...this.initialFilters,
          ...this.$route.query
        }
      }
    },
    page() {
      this.doSearch()

      this.scrollToTop()
    },
    sorting(value) {
      this.$cookies.set('jobSorting', value)
    }
  },
  async asyncData({ store, query, error }) {
    query = {
      location: 'Stockholm',
      search: '',
      roles: [],
      positionTypes: ['5b8e46b3853f039706b6ea71'],
      countryCode: 'SE',
      sorting: 'mostPopular'
    }

    try {
      const params = {
        includeSuggestions: true,
        ...query
      }

      await store.dispatch('jobs/find', params)
    } catch (err) {
      error(err)
    }
  },
  mounted() {
    this.$store.dispatch('jobs/getLatestJobs')
  },
  created() {
    // add asyncData query to $router on page load
    const query = {
      location: 'Stockholm',
      search: '',
      roles: [],
      positionTypes: ['5b8e46b3853f039706b6ea71'],
      countryCode: 'SE',
      sorting: 'mostPopular'
    }
    this.$router.push({
      query
    })
  },
  methods: {
    selectSorting(value) {
      if (value != this.sorting) {
        this.sorting = value
        this.page = 1
        this.doSearch()
      }
    },
    scrollToTop() {
      if (this.$refs.list && this.$refs.list.scrollIntoView) {
        document.body.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    onSearchBarSubmit() {
      this.filters.search = this.searchForm.search
      this.filters.location = this.searchForm.location
      this.filters.countryCode = this.searchForm.countryCode

      // when "submit" allow creation of new history items
      this.$store.dispatch('jobSearch/add', {
        params: this.params,
        createIfNotExists: true
      })

      this.doSearch()
    },

    doSearch: debounce(async function() {
      await this.$store.dispatch('jobs/find', {
        ...this.params,
        includeSuggestions: true
      })

      // only allow update of existing history items
      this.$store.dispatch('jobSearch/add', {
        params: this.params,
        createIfNotExists: false
      })

      this.$router.push({
        query: this.params
      })
    }, 100)
  }
}
</script>

<style scoped lang="scss">
.view-job-find {
  background-color: $gray-100;
}
</style>
